import type { ConsistencyDay } from '@anytracker/core'

import { formatDay } from '../format/dates.js'
import styles from './CoverageHeatmap.module.css'

interface Props {
  /** From core's `coverageDays`, across the resolved range. */
  readonly days: readonly ConsistencyDay[]
  /** The same facts in words, above the grid. Required — see below. */
  readonly caption: string
}

/**
 * Where the gaps fell (T-906).
 *
 * "22 of 30 days" is a number the report already prints; what it cannot show is
 * whether the eight missing days were one bad fortnight or a steady drizzle,
 * and that is a different fact about the same data.
 *
 * **Hidden from assistive technology, and the caption is not optional.** A grid
 * of ninety unlabelled cells is noise read aloud and each cell announced
 * separately is worse than silence — so the sentence is the only accessible
 * form of this, which makes it a required prop rather than a convention.
 *
 * Cells shrink to fit rather than the grid scrolling. Ninety days across a
 * 375px screen is about three pixels each, which is unreadable as a value and
 * still legible as a *shape* — and the shape is the whole reason for it. The
 * numbers are stated above either way.
 */
export function CoverageHeatmap({ days, caption }: Props) {
  const first = days[0]
  const last = days.at(-1)

  return (
    <div className={styles['coverage']}>
      <p className={styles['caption']}>{caption}</p>
      <div className={styles['grid']} data-testid="coverage-grid" aria-hidden="true">
        {days.map((day) => (
          <span key={day.dayKey} className={styles['cell']} data-band={day.band} />
        ))}
      </div>
      {first !== undefined && last !== undefined && (
        <p className={styles['ends']} aria-hidden="true">
          <span>{formatDay(first.dayKey)}</span>
          <span>{formatDay(last.dayKey)}</span>
        </p>
      )}
      {/* The scale in words, because the colours alone assert an order nobody
          was told about. */}
      <p className={styles['legend']} aria-hidden="true">
        <span>Nothing</span>
        <span className={styles['legendCells']}>
          {[0, 1, 2, 3].map((band) => (
            <span key={band} className={styles['cell']} data-band={band} />
          ))}
        </span>
        <span>More</span>
      </p>
    </div>
  )
}
